import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Truck, Users, MapPin, AlertTriangle, Activity, TrendingUp,
  Fuel, Wrench, Clock, Navigation, Phone, MessageSquare,
  ChevronRight, Filter, RefreshCw, Plus, Search, MoreVertical,
  CheckCircle, XCircle, AlertCircle, Zap } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { useFleetDashboard, useLiveVehicles, useAlerts, useTrips } from '../hooks/useFleetData';
import {
  getVehicleIllustration,
  CargoVanSVG,
  BoxTruckSVG,
  SemiTruckSVG,
  PickupTruckSVG,
  MotorcycleSVG } from
'../components/VehicleIllustrations';


// Status color mapping
const statusColors = {
  available: 'bg-emerald-500',
  on_route: 'bg-blue-500',
  loading: 'bg-amber-500',
  unloading: 'bg-amber-500',
  maintenance: 'bg-orange-500',
  offline: 'bg-gray-400',
  out_of_service: 'bg-red-500'
};

const statusLabels = {
  available: 'Available',
  on_route: 'On Route',
  loading: 'Loading',
  unloading: 'Unloading',
  maintenance: 'Maintenance',
  offline: 'Offline',
  out_of_service: 'Out of Service'
};

const FleetDashboard = () => {const { t } = useTranslation("fleet");
  const { dashboard, loading: dashboardLoading, refresh } = useFleetDashboard();
  const { vehicles: liveVehicles, loading: vehiclesLoading } = useLiveVehicles();
  const { alerts } = useAlerts({ resolved: false, limit: 5 });
  const { trips } = useTrips({ status: 'IN_PROGRESS', limit: 10 });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Filter vehicles by search
  const filteredVehicles = liveVehicles.filter((v) =>
  v.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  v.driver?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (dashboardLoading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>);

  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("fleetManagement", "Fleet Management")}</h1>
          <p className="text-gray-500">{t("realtimeFleetTracking", "Real-time fleet tracking and management")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button className="bg-red-500 hover:bg-red-600 text-white" size="sm">
            <Plus className="w-4 h-4 mr-2" />{t("addVehicle", "Add Vehicle")}

          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Vehicles */}
        <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t("totalVehicles", "Total Vehicles")}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboard?.vehicles?.total || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-emerald-600 font-medium">
                    {dashboard?.vehicles?.available || 0}{t("available", "available")}
                  </span>
                </div>
              </div>
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
                <Truck className="w-7 h-7 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Drivers */}
        <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t("activeDrivers", "Active Drivers")}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboard?.drivers?.on_duty || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-blue-600 font-medium">
                    {dashboard?.drivers?.total || 0}{t("total", "total")}
                  </span>
                </div>
              </div>
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* On Route */}
        <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t("onRoute", "On Route")}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboard?.vehicles?.on_route || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-emerald-600 font-medium flex items-center">
                    <Activity className="w-3 h-3 mr-1" />{t("liveTracking", "Live tracking")}

                  </span>
                </div>
              </div>
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center">
                <Navigation className="w-7 h-7 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t("activeAlerts", "Active Alerts")}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboard?.alerts?.total || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  {dashboard?.alerts?.critical > 0 ?
                  <span className="text-sm text-red-600 font-medium">
                      {dashboard.alerts.critical}{t("critical", "critical")}
                  </span> :

                  <span className="text-sm text-emerald-600 font-medium">{t("allClear", "All clear")}

                  </span>
                  }
                </div>
              </div>
              <div className={`w-14 h-14 ${dashboard?.alerts?.critical > 0 ? 'bg-red-50' : 'bg-amber-50'} rounded-2xl flex items-center justify-center`}>
                <AlertTriangle className={`w-7 h-7 ${dashboard?.alerts?.critical > 0 ? 'text-red-500' : 'text-amber-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Map Placeholder & Vehicle List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Map Card */}
          <Card className="bg-white border-0 shadow-sm overflow-hidden">
            <CardHeader className="border-b bg-gray-50 py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">{t("liveTracking", "Live Tracking")}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                    {liveVehicles.length}{t("online", "online")}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Map Placeholder - Replace with actual map component */}
              <div className="h-80 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">{t("interactiveMap", "Interactive Map")}</p>
                  <p className="text-gray-400 text-sm">{t("integrateWithLeaflet", "Integrate with Leaflet or Google Maps")}</p>
                </div>
                {/* Mini vehicle indicators */}
                {liveVehicles.slice(0, 5).map((v, idx) =>
                <div
                  key={v.id}
                  className="absolute w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform"
                  style={{
                    top: `${20 + idx * 15}%`,
                    left: `${15 + idx * 18}%`
                  }}
                  onClick={() => setSelectedVehicle(v)}>
                  
                    <Truck className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vehicle List */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">{t("fleetVehicles", "Fleet Vehicles")}</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search vehicles..."
                      className="pl-9 w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)} />
                    
                  </div>
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />{t("filter", "Filter")}

                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-96 overflow-auto">
                {filteredVehicles.length === 0 ?
                <div className="p-8 text-center">
                    <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t("noVehiclesOnline", "No vehicles online")}</p>
                  </div> :

                filteredVehicles.map((vehicle) =>
                <div
                  key={vehicle.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedVehicle(vehicle)}>
                  
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-12 bg-gray-50 rounded-xl flex items-center justify-center overflow-hidden">
                            {(() => {
                          const VehicleIcon = getVehicleIllustration(vehicle.type);
                          return <VehicleIcon className="w-full h-full" fillColor="#ffffff" />;
                        })()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{vehicle.code}</span>
                              <Badge
                            variant="secondary"
                            className={`${statusColors[vehicle.status]} text-white text-xs`}>
                            
                                {statusLabels[vehicle.status] || vehicle.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500">{vehicle.license_plate} • {vehicle.make} {vehicle.model}</p>
                            {vehicle.driver &&
                        <p className="text-sm text-gray-600 flex items-center mt-1">
                                <Users className="w-3 h-3 mr-1" />
                                {vehicle.driver}
                              </p>
                        }
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Zap className="w-4 h-4" />
                            <span className="font-medium">{vehicle.speed_kmh || 0}{t("kmh", "km/h")}</span>
                          </div>
                          {vehicle.address &&
                      <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">{vehicle.address}</p>
                      }
                        </div>
                      </div>
                    </div>
                )
                }
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Active Trips */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">{t("activeTrips", "Active Trips")}</CardTitle>
                <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                  {trips.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-64 overflow-auto">
                {trips.length === 0 ?
                <div className="p-6 text-center">
                    <Navigation className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">{t("noActiveTrips", "No active trips")}</p>
                  </div> :

                trips.map((trip) =>
                <div key={trip.id} className="p-4 hover:bg-gray-50 cursor-pointer">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{trip.trip_code}</span>
                        <Badge variant="outline" className="text-xs">
                          {trip.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{trip.origin}</span>
                        <ChevronRight className="w-3 h-3" />
                        <span className="truncate">{trip.destination}</span>
                      </div>
                      {trip.driver &&
                  <p className="text-xs text-gray-400 mt-1">{trip.driver.name}</p>
                  }
                    </div>
                )
                }
              </div>
            </CardContent>
          </Card>

          {/* Recent Alerts */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">{t("recentAlerts", "Recent Alerts")}</CardTitle>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">{t("viewAll", "View All")}

                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-64 overflow-auto">
                {alerts.length === 0 ?
                <div className="p-6 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">{t("allSystemsOperational", "All systems operational")}</p>
                  </div> :

                alerts.map((alert) =>
                <div key={alert.id} className="p-4 hover:bg-gray-50 cursor-pointer">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alert.severity === 'critical' ? 'bg-red-100' :
                    alert.severity === 'warning' ? 'bg-amber-100' : 'bg-blue-100'}`
                    }>
                          {alert.severity === 'critical' ?
                      <XCircle className="w-4 h-4 text-red-600" /> :
                      alert.severity === 'warning' ?
                      <AlertTriangle className="w-4 h-4 text-amber-600" /> :

                      <AlertCircle className="w-4 h-4 text-blue-600" />
                      }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm">{alert.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{alert.type?.replace('_', ' ')}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {alert.created_at && new Date(alert.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                )
                }
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bg-gradient-to-br from-red-500 to-red-600 border-0 shadow-sm text-white">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg mb-4">{t("todaysSummary", "Today's Summary")}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-red-100">{t("tripsCompleted", "Trips Completed")}</span>
                  <span className="font-bold text-xl">{dashboard?.trips_today?.completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-red-100">{t("totalDistance", "Total Distance")}</span>
                  <span className="font-bold text-xl">{Math.round(dashboard?.trips_today?.total_distance_km || 0)}{t("km", "km")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-red-100">{t("inProgress", "In Progress")}</span>
                  <span className="font-bold text-xl">{dashboard?.trips_today?.in_progress || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Vehicle Detail Modal */}
      {selectedVehicle &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedVehicle(null)}>
          <Card className="w-full max-w-lg m-4 bg-white" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-20 h-14 bg-gray-50 rounded-xl flex items-center justify-center overflow-hidden">
                    {(() => {
                    const VehicleIcon = getVehicleIllustration(selectedVehicle.type);
                    return <VehicleIcon className="w-full h-full" fillColor="#ffffff" />;
                  })()}
                  </div>
                  <div>
                    <CardTitle>{selectedVehicle.code}</CardTitle>
                    <p className="text-sm text-gray-500">{selectedVehicle.license_plate}</p>
                  </div>
                </div>
                <Badge className={`${statusColors[selectedVehicle.status]} text-white`}>
                  {statusLabels[selectedVehicle.status] || selectedVehicle.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t("vehicle", "Vehicle")}</p>
                  <p className="font-medium">{selectedVehicle.make} {selectedVehicle.model}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t("speed", "Speed")}</p>
                  <p className="font-medium">{selectedVehicle.speed_kmh || 0}{t("kmh", "km/h")}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t("driver", "Driver")}</p>
                  <p className="font-medium">{selectedVehicle.driver || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t("lastUpdate", "Last Update")}</p>
                  <p className="font-medium">
                    {selectedVehicle.last_update ?
                  new Date(selectedVehicle.last_update).toLocaleTimeString() :
                  'N/A'
                  }
                  </p>
                </div>
              </div>
              {selectedVehicle.address &&
            <div>
                  <p className="text-sm text-gray-500">{t("location", "Location")}</p>
                  <p className="font-medium">{selectedVehicle.address}</p>
                </div>
            }
              <div className="flex gap-2 pt-4">
                {selectedVehicle.driver_phone &&
              <Button variant="outline" className="flex-1">
                    <Phone className="w-4 h-4 mr-2" />{t("callDriver", "Call Driver")}

              </Button>
              }
                <Button variant="outline" className="flex-1">
                  <MessageSquare className="w-4 h-4 mr-2" />{t("message", "Message")}

              </Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white">
                  <Navigation className="w-4 h-4 mr-2" />{t("track", "Track")}

              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </div>);

};

export default FleetDashboard;